/**
 * Enrollments for a specific class — used by owners to manage the roster
 * and check students in/out.
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import {
  fetchClassEnrollments,
  checkIn,
  cancelEnrollment,
  enrollInClass,
} from '../../services/schedule';

export function useClassEnrollments(classId: string | null) {
  const queryClient = useQueryClient();
  const queryKey = ['class-enrollments', classId];

  const query = useQuery({
    queryKey,
    queryFn: () => fetchClassEnrollments(classId!),
    enabled: !!classId,
    staleTime: 30 * 1000,
  });

  const checkInMutation = useMutation({
    mutationFn: ({ enrollmentId }: { enrollmentId: string }) => checkIn(enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({ enrollmentId }: { enrollmentId: string }) =>
      cancelEnrollment(enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
    },
  });

  const addStudentMutation = useMutation({
    mutationFn: ({ studentId }: { studentId: string }) => {
      if (!classId) throw new Error('No class selected');
      return enrollInClass(classId, studentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
    },
  });

  return {
    enrollments: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    // Owner actions
    checkInStudent: checkInMutation.mutateAsync,
    isCheckingIn: checkInMutation.isPending,
    removeStudent: removeMutation.mutateAsync,
    isRemoving: removeMutation.isPending,
    addStudent: addStudentMutation.mutateAsync,
    isAdding: addStudentMutation.isPending,
  };
}
